import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { loadStripe } from '@stripe/stripe-js';

interface AccountProps {
  idToken: string;
}

const apiUrl = process.env.REACT_APP_API_URL;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY || '');

export default function Account({ idToken }: AccountProps) {
  const [isProUser, setIsProUser] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Check if user has pro feature flag
  useEffect(() => {
    const checkProStatus = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${apiUrl}feature/pro`, {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        });
        
        setIsProUser(response.status === 200);
      } catch (error) {
        // A 404 response means the feature flag is not enabled
        setIsProUser(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkProStatus();
  }, [idToken]);

  const handleUpgradeClick = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      
      // Call backend to create a Stripe checkout session
      const response = await axios.post(
        `${apiUrl}subscription/create-checkout-session`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      // Load Stripe and redirect to checkout
      const stripe = await stripePromise;
      if (!stripe || !response.data.sessionId) {
        throw new Error('Failed to initialize payment process');
      }

      const { error } = await stripe.redirectToCheckout({
        sessionId: response.data.sessionId
      });

      if (error) {
        throw new Error(error.message);
      }
    } catch (error: any) {
      setErrorMessage(error.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl p-4">
      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
          <CardDescription>Manage your subscription</CardDescription>
        </CardHeader>
        <CardContent>
          {errorMessage && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}
          
          {successMessage && (
            <Alert className="mb-4 bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <AlertTitle>Success</AlertTitle>
              <AlertDescription>{successMessage}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium">Organization</h3>
              <p className="text-sm text-gray-500">{/*figure out how to get the org name*/}</p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium">Subscription Status</h3>
              {isLoading ? (
                <p className="text-sm">Loading subscription information...</p>
              ) : isProUser ? (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200 mt-2">
                  <div className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                    <h4 className="font-medium">Pro Subscription</h4>
                  </div>
                  <p className="text-sm mt-1">You're currently subscribed to the Pro plan with premium features.</p>
                </div>
              ) : (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-2">
                  <h4 className="font-medium">Free Plan</h4>
                  <p className="text-sm mt-1">You're currently on the free plan with limited features.</p>
                  <div className="mt-4">
                    <Button onClick={handleUpgradeClick} disabled={isLoading}>
                      {isLoading ? 'Processing...' : 'Upgrade to Pro'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
